/**
 * Calendar pair selector.
 *
 * A sync job is ONE row pairing a Fias calendar with a provider calendar plus a
 * direction — it is not an "import" or an "export". The old UI had separate
 * "+ Import Calendar" / "+ Export Calendar" entry points AND a per-job direction
 * dropdown, which misrepresented that model badly enough that a `bidirectional`
 * job was rendered twice, once under each heading.
 *
 * So direction is chosen HERE, first, and both sides of the pair stay visible at
 * once. Switching the visible list based on direction (Google calendars for
 * import, Fias calendars for export) was considered and rejected: every job
 * needs both sides regardless of direction, so hiding one side just makes the
 * job impossible to construct.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Loader2, ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine, Lock } from 'lucide-react';
import { useRemoteCalendars } from '../hooks/useSyncConnections';
import { useCreateSyncJob, useSyncJobs } from '../hooks/useSyncJobs';
import { useCalendars } from '../hooks';
import type { RemoteCalendar } from '@/lib/api';

type Direction = 'bidirectional' | 'pull_only' | 'push_only';

const DIRECTIONS: Array<{ value: Direction; label: string; icon: typeof ArrowLeftRight }> = [
  { value: 'bidirectional', label: 'Both ways', icon: ArrowLeftRight },
  { value: 'pull_only', label: 'Import only', icon: ArrowDownToLine },
  { value: 'push_only', label: 'Export only', icon: ArrowUpFromLine },
];

/** Directions that write to the provider — blocked on read-only calendars. */
const WRITES_REMOTE: ReadonlySet<Direction> = new Set<Direction>(['bidirectional', 'push_only']);

/**
 * Google marks subscribed calendars (holidays, sports, another person's shared
 * calendar) `reader` / `freeBusyReader`. Every push to one is rejected, and the
 * failure lands in the retry inbox where nothing surfaces it — so the pairing is
 * refused up front rather than allowed to fail silently later.
 */
function isReadOnlyRemote(remote: RemoteCalendar): boolean {
  const role = remote.accessRole;
  return role === 'reader' || role === 'freeBusyReader';
}

interface RemoteCalendarPickerProps {
  connectionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Selection {
  remoteCalendar: RemoteCalendar;
  localCalendarId: string;
}

export function RemoteCalendarPicker({
  connectionId,
  open,
  onOpenChange,
}: RemoteCalendarPickerProps) {
  const {
    data: remoteCalendars,
    isLoading: loadingRemote,
    // A failed fetch previously fell through to the "no remote calendars"
    // empty state, so a 403/500 from the provider read as "your account has
    // no calendars" — it sent QA looking in entirely the wrong place.
    isError: remoteFailed,
    error: remoteError,
    refetch: refetchRemote,
  } = useRemoteCalendars(connectionId);
  const { calendars: localCalendars, loading: loadingLocal } = useCalendars();
  const { data: jobs = [] } = useSyncJobs();
  const createSyncJob = useCreateSyncJob();

  const [direction, setDirection] = useState<Direction>('bidirectional');
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [conflictStrategy, setConflictStrategy] = useState('last_write_wins');
  const [selections, setSelections] = useState<Map<string, Selection>>(new Map());
  const [error, setError] = useState<string | null>(null);

  const isLoading = loadingRemote || loadingLocal;

  /** Remote calendars already paired on this connection — one job per pair. */
  const alreadySynced = useMemo(
    () =>
      new Set(
        jobs
          .filter((j) => j.connectionId === connectionId && j.remoteResourceId)
          .map((j) => j.remoteResourceId as string),
      ),
    [jobs, connectionId],
  );

  const writesRemote = WRITES_REMOTE.has(direction);

  // Switching to a writing direction must drop any read-only calendar already
  // ticked, or the confirm would submit a pairing the provider will reject.
  useEffect(() => {
    if (!writesRemote) return;
    setSelections((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [id, sel] of prev) {
        if (isReadOnlyRemote(sel.remoteCalendar)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [writesRemote]);

  const toggleCalendar = (remote: RemoteCalendar) => {
    setSelections((prev) => {
      const next = new Map(prev);
      if (next.has(remote.id)) {
        next.delete(remote.id);
      } else {
        const defaultLocal = localCalendars.find((c) => c.isDefault);
        next.set(remote.id, {
          remoteCalendar: remote,
          localCalendarId: defaultLocal?.id ?? localCalendars[0]?.id ?? '',
        });
      }
      return next;
    });
  };

  const setLocalCalendar = (remoteId: string, localCalendarId: string) => {
    setSelections((prev) => {
      const next = new Map(prev);
      const existing = next.get(remoteId);
      if (existing) next.set(remoteId, { ...existing, localCalendarId });
      return next;
    });
  };

  const reset = () => {
    setSelections(new Map());
    setDirection('bidirectional');
    setAutoSyncEnabled(true);
    setConflictStrategy('last_write_wins');
    setError(null);
  };

  const handleConfirm = async () => {
    if (selections.size === 0) return;
    setError(null);
    try {
      await Promise.all(
        Array.from(selections.values()).map((sel) =>
          createSyncJob.mutateAsync({
            connectionId,
            localCalendarId: sel.localCalendarId,
            remoteCalendarId: sel.remoteCalendar.id,
            remoteCalendarName: sel.remoteCalendar.name,
            direction,
            autoSyncEnabled,
            // Only meaningful when both sides can change.
            conflictStrategy: direction === 'bidirectional' ? conflictStrategy : undefined,
          }),
        ),
      );
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start syncing');
    }
  };

  const handleClose = () => {
    if (!createSyncJob.isPending) {
      reset();
      onOpenChange(false);
    }
  };

  const pairLabel =
    direction === 'pull_only'
      ? 'Copy into this Fias calendar'
      : direction === 'push_only'
        ? 'Send this Fias calendar'
        : 'Keep in sync with this Fias calendar';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Select Calendars to Sync</DialogTitle>
          <DialogDescription>
            Each calendar you pick is paired with one of your Fias calendars.
          </DialogDescription>
        </DialogHeader>

        {/* Direction comes FIRST: it decides what the pairing below means, and
            (for read-only calendars) whether a pairing is possible at all. */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Direction</Label>
          <div className="grid grid-cols-3 gap-1 rounded-md bg-muted p-1">
            {DIRECTIONS.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setDirection(value)}
                disabled={createSyncJob.isPending}
                className={`flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                  direction === value
                    ? 'bg-background shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-3 w-3 shrink-0" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3 py-1 max-h-[320px] overflow-y-auto">
            {remoteFailed ? (
              <div className="space-y-3 rounded-md border border-destructive/50 p-3">
                <p className="text-sm font-medium text-destructive">
                  Couldn&apos;t load calendars from the provider.
                </p>
                <p className="text-sm text-muted-foreground">
                  {remoteError instanceof Error && remoteError.message
                    ? remoteError.message
                    : 'The provider rejected the request.'}{' '}
                  If this account was just connected, check that you allowed the calendar permission
                  — reconnecting lets you grant it.
                </p>
                <Button variant="outline" size="sm" onClick={() => void refetchRemote()}>
                  Try again
                </Button>
              </div>
            ) : !remoteCalendars?.length ? (
              // Reached when discovery SUCCEEDS but nothing came back: the
              // account has a calendar home with no event calendars in it
              // (a Reminders-only account, say). Saying only "none found"
              // leaves the user with nowhere to go, and this state is easily
              // mistaken for a failure — so name the likely cause.
              <div className="space-y-1.5 py-2">
                <p className="text-sm text-muted-foreground">No calendars to sync.</p>
                <p className="text-xs text-muted-foreground">
                  This account connected successfully but has no event calendars. Create one in the
                  Calendar app on an Apple device (or at icloud.com/calendar), then try again.
                </p>
              </div>
            ) : (
              remoteCalendars.map((remote) => {
                const selected = selections.has(remote.id);
                const selection = selections.get(remote.id);
                const synced = alreadySynced.has(remote.id);
                const readOnly = isReadOnlyRemote(remote);
                const blockedByDirection = readOnly && writesRemote;
                const disabled = synced || blockedByDirection || createSyncJob.isPending;

                return (
                  <div
                    key={remote.id}
                    className={`flex items-start gap-3 rounded-md border p-3 ${
                      disabled && !createSyncJob.isPending ? 'opacity-60' : ''
                    }`}
                  >
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => toggleCalendar(remote)}
                      disabled={disabled}
                      aria-label={`Sync ${remote.name}`}
                    />

                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2">
                        {remote.color && (
                          <span
                            className="inline-block h-3 w-3 rounded-full shrink-0"
                            style={{ backgroundColor: remote.color }}
                          />
                        )}
                        <span className="text-sm font-medium min-w-0 break-words">
                          {remote.name}
                          {remote.primary && (
                            <span className="ml-1.5 text-xs text-muted-foreground">(Primary)</span>
                          )}
                        </span>
                        {readOnly && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                            <Lock className="h-3 w-3" />
                            Read-only
                          </span>
                        )}
                      </div>

                      {synced && (
                        <p className="text-[11px] text-muted-foreground">
                          Already syncing. Remove it below to pair it differently.
                        </p>
                      )}

                      {blockedByDirection && !synced && (
                        <p className="text-[11px] text-muted-foreground">
                          This calendar is read-only, so Fias can&apos;t write to it. Choose
                          &ldquo;Import only&rdquo; to sync it.
                        </p>
                      )}

                      {selected && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{pairLabel}</Label>
                          <Select
                            value={selection?.localCalendarId ?? ''}
                            onValueChange={(val) => setLocalCalendar(remote.id, val)}
                            disabled={createSyncJob.isPending}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Choose calendar..." />
                            </SelectTrigger>
                            <SelectContent>
                              {localCalendars.map((cal) => (
                                <SelectItem key={cal.id} value={cal.id}>
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                                      style={{ backgroundColor: cal.color }}
                                    />
                                    {cal.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Job settings, shared by everything selected in this pass. Each job
            keeps its own copy and can be changed individually afterwards. */}
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">Auto-sync</Label>
              <p className="text-[11px] text-muted-foreground">
                Keep syncing in the background, not just when you press Sync now.
              </p>
            </div>
            <Switch
              checked={autoSyncEnabled}
              onCheckedChange={setAutoSyncEnabled}
              disabled={createSyncJob.isPending}
              aria-label="Auto-sync"
            />
          </div>

          {/* Conflicts only arise when BOTH sides can change. */}
          {direction === 'bidirectional' && (
            <div className="flex items-center justify-between gap-3">
              <Label className="text-xs shrink-0">Conflicts</Label>
              <Select
                value={conflictStrategy}
                onValueChange={setConflictStrategy}
                disabled={createSyncJob.isPending}
              >
                <SelectTrigger className="h-8 w-[180px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_write_wins">Latest wins</SelectItem>
                  <SelectItem value="local_wins">Fias wins</SelectItem>
                  <SelectItem value="remote_wins">Provider wins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {writesRemote && selections.size > 0 && (
            <p className="text-[11px] text-muted-foreground">
              Events already in the selected Fias calendar
              {selections.size > 1 ? 's' : ''} will be sent to the provider on the first sync.
            </p>
          )}
        </div>

        {error && <p className="text-sm text-destructive break-words">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={createSyncJob.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selections.size === 0 || createSyncJob.isPending}
          >
            {createSyncJob.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Sync{' '}
            {selections.size > 0
              ? `${selections.size} Calendar${selections.size > 1 ? 's' : ''}`
              : 'Calendar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
